import React from 'react';
import { Collaborator } from '@/features/Collaborators/Models/collaborator';
import { StatusCards } from '@/components/shared/StatusCards';
import { useStatusCards } from '../hooks/useStatusCards';
import { useSalaryCards } from '../hooks/useSalaryCards';
import type { CardFilter } from '@/hooks/useFilterSystem';
import { ALLOCATION_STATUS_ORDER, ALLOCATION_STATUS_METADATA } from '../constants/allocationStatus';

interface PresetScopeRendererProps {
    activePreset: string | null;
    collaborators: Collaborator[]; // Dados filtrados pela camada base (para cálculo de contadores)
    onCardFilterChange?: (cardFilter: CardFilter | null) => void;
}

export const PresetScopeRenderer: React.FC<PresetScopeRendererProps> = ({
    activePreset,
    collaborators,
    onCardFilterChange,
}) => {
    // Estado local para controlar qual card está ativo
    const [activeCardFilter, setActiveCardFilter] = React.useState<CardFilter | null>(null);

    // Hooks para cálculo de estatísticas (apenas contadores, sem filtro interno)
    const { stats } = useStatusCards(collaborators);
    const { stats: salaryStats } = useSalaryCards(collaborators);
    const allocationStats = React.useMemo(() => {
        const total = collaborators.length;
        if (total === 0) {
            return [];
        }

        return ALLOCATION_STATUS_ORDER.map(status => {
            const matching = collaborators.filter(collaborator => collaborator.allocationPreview?.status === status).length;
            const percentage = Math.round((matching / total) * 100);

            return {
                name: ALLOCATION_STATUS_METADATA[status].title,
                field: status,
                stat: `${percentage}%`,
                updated: matching,
                total,
                filterState: 'none' as const,
            };
        });
    }, [collaborators]);

    // Handlers que comunicam com o sistema de filtros
    const handleStatusClick = React.useCallback((field: string) => {
        // Ciclo: none -> not_updated -> updated -> none
        let newState: 'updated' | 'not_updated' | null = 'not_updated';
        
        if (activeCardFilter?.type === 'status' && activeCardFilter.field === field) {
            if (activeCardFilter.state === 'not_updated') {
                newState = 'updated';
            } else {
                newState = null; // Clear filter
            }
        }
        
        const newFilter = newState ? {
            type: 'status' as const,
            field,
            state: newState
        } : null;
        
        setActiveCardFilter(newFilter);
        
        if (onCardFilterChange) {
            onCardFilterChange(newFilter);
        }
    }, [activeCardFilter, onCardFilterChange]);

    const handleSalaryClick = React.useCallback((field: string) => {
        // Ciclo: none -> not_updated -> updated -> none
        let newState: 'updated' | 'not_updated' | null = 'not_updated';
        
        if (activeCardFilter?.type === 'salary' && activeCardFilter.field === field) {
            if (activeCardFilter.state === 'not_updated') {
                newState = 'updated';
            } else {
                newState = null; // Clear filter
            }
        }
        
        const newFilter = newState ? {
            type: 'salary' as const,
            field,
            state: newState
        } : null;
        
        setActiveCardFilter(newFilter);
        
        if (onCardFilterChange) {
            onCardFilterChange(newFilter);
        }
    }, [activeCardFilter, onCardFilterChange]);

    const handleAllocationClick = React.useCallback((field: string) => {
        // Ciclo: none -> updated -> not_updated -> none
        let newState: 'updated' | 'not_updated' | null = 'updated';

        if (activeCardFilter?.type === 'allocation' && activeCardFilter.field === field) {
            if (activeCardFilter.state === 'updated') {
                newState = 'not_updated';
            } else {
                newState = null;
            }
        }

        const newFilter = newState ? {
            type: 'allocation' as const,
            field,
            state: newState
        } : null;

        setActiveCardFilter(newFilter);

        if (onCardFilterChange) {
            onCardFilterChange(newFilter);
        }
    }, [activeCardFilter, onCardFilterChange]);

    // Adicionar estado visual aos stats baseado no filtro ativo
    const statusStatsWithFilter = React.useMemo(() => {
        return stats.map(stat => ({
            ...stat,
            filterState: activeCardFilter?.type === 'status' && activeCardFilter.field === stat.field 
                ? activeCardFilter.state as 'updated' | 'not_updated'
                : 'none' as const
        }));
    }, [stats, activeCardFilter]);
    
    const salaryStatsWithFilter = React.useMemo(() => {
        return salaryStats.map(stat => ({
            ...stat,
            filterState: activeCardFilter?.type === 'salary' && activeCardFilter.field === stat.field 
                ? activeCardFilter.state as 'updated' | 'not_updated'
                : 'none' as const
        }));
    }, [salaryStats, activeCardFilter]);

    const allocationStatsWithFilter = React.useMemo(() => {
        return allocationStats.map(stat => ({
            ...stat,
            filterState: activeCardFilter?.type === 'allocation' && activeCardFilter.field === stat.field
                ? activeCardFilter.state as 'updated' | 'not_updated'
                : 'none' as const
        }));
    }, [allocationStats, activeCardFilter]);

    // Render scope component based on active preset
    switch (activePreset) {
        case 'cadastral_update':
            return (
                <StatusCards
                    stats={statusStatsWithFilter}
                    onCardClick={handleStatusClick}
                    desktopColumns={4}
                />
            );
            
        case 'salary_management':
            return (
                <StatusCards
                    stats={salaryStatsWithFilter}
                    onCardClick={handleSalaryClick}
                    desktopColumns={4}
                />
            );
        
        case 'allocation_management':
            return (
                <StatusCards
                    stats={allocationStatsWithFilter}
                    onCardClick={handleAllocationClick}
                    desktopColumns={5}
                />
            );
        
        // Future presets can be added here
        // case 'hr_management':
        //     return <HRScopeComponent ... />;
        
        default:
            return null; // No scope component for this preset
    }
};

export default PresetScopeRenderer;
